import { useCallback, useEffect, useId, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * The site key comes from the API (GET api/Research/Config) rather than being
 * baked in here, so the key lives in one place and the same build works against
 * any environment. The token it produces is single-use and short-lived, so the
 * widget is reset after every submission attempt — hence the `resetSignal`
 * prop, which the forms bump whenever a request comes back.
 */

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      action?: string;
    }
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * How long to wait for a token before deciding the widget is not going to
 * produce one. A widget on a hostname that is not on the key's allow-list
 * renders but never resolves and never calls error-callback, so a timeout is
 * the only way to notice. The server still verifies every request, so treating
 * a stuck widget as unavailable loosens nothing.
 */
const RENDER_TIMEOUT_MS = 8000;

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile'));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

interface TurnstileProps {
  siteKey: string;
  action?: string;
  /** Change this value to clear the current token and re-arm the widget. */
  resetSignal?: number;
  onToken: (token: string) => void;
  /**
   * Called when the widget cannot run at all — the script is blocked, or this
   * hostname is not on the key's allow-list. The form then stops holding the
   * submit button hostage and lets the server decide, which it does anyway.
   */
  onUnavailable?: (unavailable: boolean) => void;
}

export function Turnstile({
  siteKey,
  action,
  resetSignal = 0,
  onToken,
  onUnavailable
}: TurnstileProps) {
  const holder = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const timeoutId = useRef<number | null>(null);
  const gotToken = useRef(false);
  const latestOnToken = useRef(onToken);
  const latestOnUnavailable = useRef(onUnavailable);
  const [failed, setFailed] = useState(false);
  const fallbackId = useId();

  latestOnToken.current = onToken;
  latestOnUnavailable.current = onUnavailable;

  const markUnavailable = useCallback(() => {
    setFailed(true);
    latestOnUnavailable.current?.(true);
  }, []);

  const armTimeout = useCallback(() => {
    if (timeoutId.current) window.clearTimeout(timeoutId.current);
    timeoutId.current = window.setTimeout(() => {
      if (!gotToken.current) markUnavailable();
    }, RENDER_TIMEOUT_MS);
  }, [markUnavailable]);

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) return;

    loadScript()
      .then(() => {
        if (cancelled || !holder.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(holder.current, {
          sitekey: siteKey,
          action,
          theme: 'light',
          callback: (token) => {
            gotToken.current = true;
            if (timeoutId.current) window.clearTimeout(timeoutId.current);
            setFailed(false);
            latestOnUnavailable.current?.(false);
            latestOnToken.current(token);
          },
          'expired-callback': () => {
            gotToken.current = false;
            latestOnToken.current('');
            armTimeout();
          },
          'error-callback': () => {
            latestOnToken.current('');
            markUnavailable();
          }
        });
        armTimeout();
      })
      .catch(() => {
        if (!cancelled) markUnavailable();
      });

    return () => {
      cancelled = true;
      if (timeoutId.current) window.clearTimeout(timeoutId.current);
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* the widget may already be gone */
        }
        widgetId.current = null;
      }
    };
  }, [siteKey, action, armTimeout, markUnavailable]);

  useEffect(() => {
    if (resetSignal === 0) return;
    gotToken.current = false;
    latestOnToken.current('');
    if (widgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetId.current);
        armTimeout();
      } catch {
        /* nothing to reset yet */
      }
    }
  }, [resetSignal, armTimeout]);

  if (!siteKey) return null;

  return (
    <div className="turnstile-field">
      <div ref={holder} id={`turnstile-${fallbackId}`} />
      {failed ? (
        <p className="error-text" role="alert">
          The security check could not load. You can still continue — the server will
          verify the request.
        </p>
      ) : null}
    </div>
  );
}
